import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth-context";
import { LinkUserApi } from "../../service/Api/LinkUserApi";
import { ApplicationApi } from "../../service/Api/ApplicationsApi";
import "./LinkUser.scss";

const LinkUser = () => {
  const navigate = useNavigate();
  const { userManager, userRole } = useAuth();
  const [searchBy, setSearchBy] = useState("username");
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkMessage, setLinkMessage] = useState("");
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");

  const searchOptions = [
    {
      key: "username",
      label: "Username",
      icon: "fh_user_fill",
      placeholder: "Enter username",
      emptyMessage: "Please enter a username",
    },
    {
      key: "email",
      label: "Email",
      icon: "fh_email_fill",
      placeholder: "Enter email address",
      emptyMessage: "Please enter an email",
    },
    {
      key: "ssoguid",
      label: "SSO GUID",
      icon: "fh_id_card",
      placeholder: "Enter SSO GUID",
      emptyMessage: "Please enter an SSO GUID",
    },
  ];

  const activeSearchOption =
    searchOptions.find((option) => option.key === searchBy) || searchOptions[0];

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await ApplicationApi.getUserRole();
        setRoles(response || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, []);

  const handleSearchByChange = (key) => {
    setSearchBy(key);
    setSearchValue("");
    setErrorMessage("");
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    const value = searchValue.trim();

    if (!value) {
      setFoundUser(null);
      setErrorMessage(activeSearchOption.emptyMessage);
      return;
    }

    setErrorMessage("");
    setFoundUser(null);
    setSelectedRole("");
    setLinkMessage("");
    setLinkSuccess(false);
    setLoading(true);

    try {
      let response;
      if (searchBy === "username") {
        response = await LinkUserApi.findUserByName(value);
      } else if (searchBy === "email") {
        response = await LinkUserApi.findUserByEmail(value);
      } else {
        response = await LinkUserApi.findUserById(value);
      }

      if (!response) {
        setFoundUser(null);
        setErrorMessage("User not found in SSO");
        return;
      }

      setFoundUser(response);
    } catch (error) {
      console.error("Link User search error:", error);
      setFoundUser(null);

      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const responseText =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || responseData?.title || "";

      if (
        searchBy === "email" &&
        status === 400 &&
        String(responseText).includes("Sequence contains more than one element")
      ) {
        setErrorMessage(
          "This Email has multiple accounts, please search with Username or SSO GUID",
        );
        return;
      }

      setErrorMessage(
        status === 204
          ? "User not found in SSO"
          : responseData?.message ||
              "Unable to find user. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const createPortalUser = async () => {
    const createUserData = {
      FirstName: foundUser.firstName,
      LastName: foundUser.lastName,
      Username: foundUser.userName,
      Email: foundUser.email,
      UserID_SSO: foundUser.id,
      RoleID: selectedRole,
      IsActive: "True",
      IsAdmin: true,
      isCommunicationActive: false,
    };

    const createResponse = await ApplicationApi.createNewUser(createUserData);
    if (createResponse === undefined) {
      throw new Error("Failed to create user in local DB.");
    }

    setLinkSuccess(true);
    setLinkMessage("User mapped successfully");
  };

  const findPortalUserByEmail = async (email) => {
    if (!email) {
      return null;
    }

    const response = await ApplicationApi.getPortalUserList(
      "",
      "",
      "",
      email,
      "",
      1,
      10,
      userRole,
    );

    const items = response?.items || [];
    return items.length > 0 ? items[0] : null;
  };

  const linkUnmappedUser = async () => {
    const loggedInUser = await userManager.getUser();
    const createdBy = loggedInUser?.profile?.fullname
      ? loggedInUser.profile.fullname.replace(/,/g, "")
      : "";

    await LinkUserApi.addClientUserMapping({
      UserId: foundUser.id,
      ClientName: process.env.REACT_APP_MEMBER_APP_OIDC_CLIENTID,
      IsActive: true,
      CreatedById: foundUser.id,
      CreatedBy: createdBy,
    });

    await createPortalUser();
  };

  const handleLinkUser = async () => {
    if (!foundUser?.id) {
      return;
    }

    if (!selectedRole) {
      setLinkSuccess(false);
      setLinkMessage("Please select a Role");
      return;
    }

    setLinkMessage("");
    setLinkSuccess(false);
    setLinking(true);

    try {
      let isMappedInSSO = false;
      try {
        const mappingResponse = await LinkUserApi.getClientUserMapping(
          foundUser.id,
        );
        isMappedInSSO = !!mappingResponse;
      } catch (mappingError) {
        if (mappingError?.response?.status !== 204) {
          throw mappingError;
        }
        isMappedInSSO = false;
      }

      const portalUser = await findPortalUserByEmail(foundUser.email);

      // Already in portal (with or without SSO mapping) → do not re-run full link
      if (portalUser) {
        setLinkSuccess(false);
        setLinkMessage("User is already mapped with the system");
        return;
      }

      // Mapped in SSO but missing in portal → create portal entry only
      if (isMappedInSSO) {
        await createPortalUser();
        return;
      }

      // Not mapped and not in portal → full link flow
      await linkUnmappedUser();
    } catch (error) {
      console.error("Link User error:", error);
      setLinkMessage(
        error?.response?.data?.message ||
          error.message ||
          "Unable to link user. Please try again.",
      );
    } finally {
      setLinking(false);
    }
  };

  const displayName =
    foundUser?.fullName ||
    [foundUser?.firstName, foundUser?.lastName].filter(Boolean).join(" ") ||
    "-";

  return (
    <div className="main-content link-user-page">
      <div className="content-header link-user-page-header">
        <div className="link-user-page-heading">
          <h1 className="page-header mb-1">Link Existing SSO User</h1>
          <p className="link-user-subtitle mb-0">
            Search for an existing user in SSO and grant Admin Portal access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/UserSearch")}
          className="btn btn-border-primary h-48 d-flex align-items-center link-user-back-btn"
        >
          <i className="fh_arrow_left icon-mr fs-5"></i>
          <span>Back to User list</span>
        </button>
      </div>

      <div className="row g-3 g-lg-4">
        <div className="col-12 col-xl-6">
          <div className="card link-user-card h-100">
            <div className="card-body">
              <div className="link-user-search-header">
                <div className="link-user-search-icon">
                  <i className="fh_user_search_1"></i>
                </div>
                <div>
                  <h2 className="link-user-card-title">Search for User</h2>
                  <p className="link-user-card-desc mb-0">
                    Choose a search method and find an existing SSO user.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSearch} className="link-user-search-form">
                <div
                  className="link-user-search-tabs"
                  role="tablist"
                  aria-label="Search method"
                >
                  {searchOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      role="tab"
                      aria-selected={searchBy === option.key}
                      className={`link-user-search-tab ${searchBy === option.key ? "active" : ""
                        }`}
                      onClick={() => handleSearchByChange(option.key)}
                    >
                      <i className={option.icon}></i>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>

                <label htmlFor="searchValue" className="form-label">
                  Find by {activeSearchOption.label}
                </label>
                <div className="link-user-search-input">
                  <i className={activeSearchOption.icon}></i>
                  <input
                    type={searchBy === "email" ? "email" : "text"}
                    id="searchValue"
                    name="searchValue"
                    className="form-control"
                    placeholder={activeSearchOption.placeholder}
                    value={searchValue}
                    onChange={(e) => {
                      setErrorMessage("");
                      setSearchValue(e.target.value);
                    }}
                    autoComplete="off"
                  />
                </div>

                {errorMessage && (
                  <p
                    className={`mb-3 ${errorMessage === "User not found in SSO"
                      ? "link-user-not-found-text"
                      : "link-user-error"
                      }`}
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                )}
                <div className="link-user-info-banner">
                  <i className="fh_info_fill"></i>
                  <div>
                    <p className="mb-1 fw-semibold">
                      Search looks up users in the connected SSO system.
                    </p>
                    <p className="mb-0">
                      After finding a user, assign a role and grant Admin Portal
                      access.
                    </p>
                  </div>
                </div>
                <div className="link-user-search-actions">
                  <button
                    type="submit"
                    className="btn btn-primary h-48 d-flex align-items-center justify-content-center"
                    disabled={loading}
                  >
                    <i className="fh_search icon-mr"></i>
                    <span>{loading ? "Searching..." : "Search User"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {foundUser && (
          <div className="col-12 col-xl-6">
            <div className="card link-user-result-card h-100">
              <div className="card-body">
                <div className="link-user-search-header">
                  <div className="link-user-search-icon">
                    <i className="fh_user_fill"></i>
                  </div>
                  <div>
                    <h2 className="link-user-card-title">User Information</h2>
                    <p className="link-user-found-text mb-0" role="status">
                      User found in SSO
                    </p>
                  </div>
                </div>

                <div className="link-user-profile">
                  <div className="link-user-avatar" aria-hidden="true">
                    {(displayName || "U")
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase())
                      .join("") || "U"}
                  </div>
                  <div className="link-user-profile-meta">
                    <h3 className="link-user-profile-name">{displayName}</h3>
                    <div className="link-user-profile-badges">
                      <span
                        className={`link-user-badge ${foundUser.isEnabled
                          ? "link-user-badge-active"
                          : "link-user-badge-inactive"
                          }`}
                      >
                        {foundUser.isEnabled ? "Active" : "Inactive"}
                      </span>
                      <span
                        className={`link-user-badge ${foundUser.isAdmin
                          ? "link-user-badge-assigned"
                          : "link-user-badge-not-assigned"
                          }`}
                      >
                        {foundUser.isAdmin
                          ? "Admin Access Assigned"
                          : "Admin Access Not Assigned"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="link-user-details">
                  <div className="link-user-detail-row">
                    <span className="link-user-detail-label">Username</span>
                    <span className="link-user-detail-value">
                      {foundUser.userName || "-"}
                    </span>
                  </div>
                  <div className="link-user-detail-row">
                    <span className="link-user-detail-label">Email</span>
                    <span className="link-user-detail-value">
                      {foundUser.email || "-"}
                    </span>
                  </div>
                  <div className="link-user-detail-row">
                    <span className="link-user-detail-label">Department</span>
                    <span className="link-user-detail-value">
                      {foundUser.jobTitle || "-"}
                    </span>
                  </div>
                </div>

                <div className="link-user-role-field">
                  <label htmlFor="UserRole" className="form-label">
                    Role<span className="text-danger">*</span>
                  </label>
                  <div className="form-field position-relative">
                    <select
                      id="UserRole"
                      className="form-control"
                      name="UserRole"
                      value={selectedRole}
                      onChange={(e) => {
                        setSelectedRole(e.target.value);
                        setLinkMessage("");
                        setLinkSuccess(false);
                      }}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.roleID_PK} value={role.roleID_PK}>
                          {role.roleName}
                        </option>
                      ))}
                    </select>
                    <span className="field-icon position-absolute top-50 translate-middle-y">
                      <i className="fh_arrow_down_line"></i>
                    </span>
                  </div>
                </div>

                {linkMessage && (
                  <div
                    className={
                      linkSuccess
                        ? "link-user-success mt-3 mb-0"
                        : "link-user-error mt-3 mb-0"
                    }
                    role="alert"
                  >
                    {linkMessage}
                  </div>
                )}

                <div className="link-user-result-actions">
                  <button
                    type="button"
                    className="btn btn-primary h-48 d-flex align-items-center justify-content-center"
                    onClick={handleLinkUser}
                    disabled={linking}
                  >
                    <i className="fh_link icon-mr"></i>
                    <span>{linking ? "Linking..." : "Link User"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkUser;
